import React, {useCallback, useEffect, useRef, useState} from "react";
import {adminApi} from "@/boxhub-api";
import {useSnackbarEx} from "@/lib/snackbarex";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {DialogEx} from "@/lib/components/DialogEx";
import {LogDTO, LogEntryDTO} from "@/api/data-contracts";
import {DataTable} from "@/lib/components/DataTable";
import {ColumnDef} from "@tanstack/react-table";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import PageLoadingSpinner from "@/lib/components/PageLoadingSpinner";
import {Grid2, TablePagination} from "@mui/material";
import {Layout, LayoutItem} from "@/lib/components/Layout";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {useTranslation} from "react-i18next";
import {AdminI18N} from "@/translations";

interface LogViewerDialogProps {
    open: boolean;
    onClose: () => void;
}

export const LogViewerDialog: React.FC<LogViewerDialogProps> = ({open, onClose}) => {
    const {showMessage} = useSnackbarEx();
    const {t, i18n} = useTranslation();
    const locale = i18n.language;
    const theme = useTheme();
    const [log, setLog] = useState<LogDTO>();
    const [busy, setBusy] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(500);

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
            () => () => {
                if (scrollTimerRef.current !== null) {
                    clearTimeout(scrollTimerRef.current);
                }
            },
            []
    );

    const scrollToBottom = useCallback(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
        }
    }, []);

    /**
     * Loads a page of log entries from the server.
     * @param file - Log file name; falls back to the currently loaded file if omitted.
     * @param offset - Zero-based line offset. Pass -1 (default) to request the tail (last `limit` lines).
     * @param limit - Number of lines to fetch. Defaults to the current rowsPerPage.
     */
    const loadLog = useCallback(
            async (file?: string, offset: number = -1, limit: number = rowsPerPage) => {
                setBusy(true);
                try {
                    const res = await adminApi.log({file: file || log?.file, offset, limit});
                    const data = res.data;
                    setLog(data);
                    // Compute page from the effective offset returned by the backend.
                    // For tail requests (offset=-1 sent), the backend returns the actual offset used.
                    if (data?.offset != null && limit > 0) {
                        setPage(Math.floor(data.offset / limit));
                    } else {
                        setPage(0);
                    }
                    scrollTimerRef.current = setTimeout(() => {
                        if (tableContainerRef.current) {
                            scrollToBottom();
                        }
                    }, 200);
                } catch (error: unknown) {
                    showMessage({
                        summary: t(AdminI18N.logViewer.message.loadFailed),
                        severity: "error",
                        error
                    });
                } finally {
                    setBusy(false);
                }
            },
            [log?.file, rowsPerPage, scrollToBottom, showMessage, t]
    );

    const paginatedData = log?.lines ?? [];

    const handleChangePage = (_event: unknown, newPage: number) => {
        const newOffset = newPage * rowsPerPage;
        loadLog(log?.file, newOffset, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newLimit = Number.parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        loadLog(log?.file, 0, newLimit);
    };

    const initialLoadDone = useRef(false);
    const initialRowsPerPage = useRef(rowsPerPage);
    useEffect(() => {
        if (open && !initialLoadDone.current) {
            initialLoadDone.current = true;
            loadLog(undefined, -1, initialRowsPerPage.current);
        }
        // intentional: initial load only — rowsPerPage changes are handled via handleChangeRowsPerPage
    }, [open, loadLog]);

    const totalCount = log?.totalLines ?? log?.lines?.length ?? 0;

    const buttons = (
            <ButtonEx label={t(AdminI18N.logViewer.action.refresh)} data-cy="refresh-button" onClick={() => loadLog()}
                      color="secondary"/>
    );

    const columns: ColumnDef<LogEntryDTO>[] = [
        {
            accessorKey: "timestamp",
            header: "Timestamp",
            cell: (info) => {
                const value = String(info.getValue() ?? "");
                return new Date(value).toLocaleString(locale, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    fractionalSecondDigits: 3
                });
            }
        },
        {
            accessorKey: "thread",
            header: "Thread"
        },
        {
            accessorKey: "level",
            header: "Level"
        },
        {
            accessorKey: "message",
            header: "Message"
        },
        {
            accessorKey: "logger",
            header: "Logger"
        },
        {
            accessorKey: "exceptionClass",
            header: "Exception"
        },
        {
            accessorKey: "stackTrace",
            header: "Stack Trace",
            cell: (info) => info.getValue() || "N/A",
            size: 20,
            maxSize: 20
        }
    ];
    return (
            <DialogEx
                    title={t(AdminI18N.logViewer.page.title, {count: totalCount})}
                    data-cy={"log-dialog"}
                    open={open}
                    onClose={onClose}
                    maxWidth={false}
                    fullWidth
                    buttons={buttons}
                    contentSx={{overflow: "hidden"}}
                    allowFullScreen
            >
                {busy && <PageLoadingSpinner/>}
                <Layout root>
                    <LayoutItem>
                        <Grid2 container spacing={2} justifyContent="space-between">
                            <Grid2>
                                <SelectInputField
                                        name="log"
                                        label={t(AdminI18N.logViewer.field.logFile)}
                                        value={log?.file}
                                        variant="standard"
                                        options={log?.files?.map((o) => ({label: o, value: o})) ?? []}
                                        onChange={(e) => {
                                            loadLog(e.target.value);
                                        }}
                                />
                            </Grid2>
                            <Grid2>
                                <TablePagination
                                        component="div"
                                        count={totalCount}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[50, 100, 500]}
                                />
                            </Grid2>
                        </Grid2>
                    </LayoutItem>
                    <LayoutItem
                            body
                            sx={{
                                "& table": {
                                    tableLayout: "auto",
                                    width: "100%",
                                    maxWidth: "100%"
                                },
                                "& th,& td": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                    paddingTop: 0.5,
                                    paddingBottom: 0.5,
                                    maxWidth: 800
                                },
                                "& th": {
                                    fontWeight: "bold",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 12
                                }
                            }}
                    >
                        <DataTable
                                columns={columns}
                                data={paginatedData}
                                noResultsValue={t(AdminI18N.logViewer.message.noLog)}
                                striped
                                expandable
                                maxHeight={"80vh"}
                                scrollRef={tableContainerRef as React.RefObject<HTMLDivElement>}
                                renderRowSubComponent={(row) => {
                                    const rowData = row.original;
                                    return (
                                            <Box
                                                    sx={{
                                                        padding: "8px",
                                                        backgroundColor:
                                                                theme.palette.mode === "light"
                                                                        ? "rgba(249, 249, 249, 0.7)"
                                                                        : "rgba(30, 40, 60, 0.5)"
                                                    }}
                                            >
                                                <dl
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "100px 1fr",
                                                            gap: "0",
                                                            margin: 0
                                                        }}
                                                >
                                                    {Object.entries(rowData).map(([key, value]) => (
                                                            <React.Fragment key={key}>
                                                                <dt
                                                                        style={{
                                                                            fontWeight: "bold",
                                                                            padding: "4px",
                                                                            border: `1px solid ${theme.palette.mode === "light" ? "rgba(221, 221, 221, 0.8)" : "rgba(70, 80, 100, 0.8)"}`,
                                                                            textAlign: "right",
                                                                            verticalAlign: "top"
                                                                        }}
                                                                >
                                                                    {key}
                                                                </dt>
                                                                <dd
                                                                        style={{
                                                                            padding: "4px",
                                                                            border: `1px solid ${theme.palette.mode === "light" ? "rgba(221, 221, 221, 0.8)" : "rgba(70, 80, 100, 0.8)"}`,
                                                                            textAlign: "left",
                                                                            verticalAlign: "top",
                                                                            margin: 0
                                                                        }}
                                                                >
                                                                    {typeof value === "object" ? (
                                                                            <pre>{JSON.stringify(value, null, 2)}</pre>
                                                                    ) : (
                                                                            value?.toString() || "N/A"
                                                                    )}
                                                                </dd>
                                                            </React.Fragment>
                                                    ))}
                                                </dl>
                                            </Box>
                                    );
                                }}
                        />
                    </LayoutItem>
                </Layout>
            </DialogEx>
    );
};
